import type {
  CreateDriverInput,
  DriverDocumentAttachInput,
  DriverDocumentUploadUrlInput,
  DriverListQuery,
  DriverStatusChangeInput,
  EmploymentStatus,
  Page,
  UpdateDriverInput,
} from '@chukua/common';
import { api } from '../../lib/api';
import { toListParams } from '../../lib/list-params';

export interface DriverListItem {
  id: string;
  name: string;
  phone: string;
  licenceNumber: string;
  employmentStatus: EmploymentStatus;
  createdAt: string;
}

export interface DriverView {
  id: string;
  name: string;
  phone: string;
  nationalId: string;
  dateOfBirth: string;
  licenceNumber: string;
  licenceClasses: string[];
  hazmatCertified: boolean;
  employmentStatus: EmploymentStatus;
  employmentStatusEffectiveAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DriverDocumentListItem {
  documentTypeCode: string;
  contentType: string;
  issuedAt: string | null;
  expiresAt: string | null;
  uploadedBy: { id: string; name: string } | null;
  updatedAt: string;
}

/** Not built yet (M6/M5) — shown as unavailable rather than a guessed shape. */
export interface UnavailablePlaceholder {
  available: false;
}

export interface DriverDetail extends DriverView {
  documents: DriverDocumentListItem[];
  advanceBalance: UnavailablePlaceholder;
  behaviourScore: UnavailablePlaceholder;
}

export interface DriverDocumentType {
  id: string;
  code: string;
  name: string;
  appliesTo: string;
  legalHardBlock: boolean;
  crossBorderOnly: boolean;
  typicalValidityDays: number;
  reminderLeadDays: number;
  isActive: boolean;
}

export function listDrivers(query: DriverListQuery): Promise<Page<DriverListItem>> {
  return api<Page<DriverListItem>>(`/drivers?${toListParams(query)}`);
}

export async function createDriver(input: CreateDriverInput): Promise<void> {
  await api<void>('/drivers', { method: 'POST', body: JSON.stringify(input) });
}

export function getDriver(id: string): Promise<DriverDetail> {
  return api<DriverDetail>(`/drivers/${id}`);
}

export async function updateDriver(id: string, input: UpdateDriverInput): Promise<void> {
  await api<void>(`/drivers/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function changeDriverStatus(id: string, input: DriverStatusChangeInput): Promise<void> {
  await api<void>(`/drivers/${id}/status`, { method: 'POST', body: JSON.stringify(input) });
}

export function listDriverDocumentTypes(): Promise<DriverDocumentType[]> {
  return api<DriverDocumentType[]>('/driver-document-types');
}

export interface PresignedUpload {
  key: string;
  url: string;
  fields: Record<string, string>;
  expiresAt: string;
}

export function createDriverDocumentUploadUrl(driverId: string, input: DriverDocumentUploadUrlInput): Promise<PresignedUpload> {
  return api<PresignedUpload>(`/drivers/${driverId}/documents/upload-url`, { method: 'POST', body: JSON.stringify(input) });
}

export async function attachDriverDocument(driverId: string, input: DriverDocumentAttachInput): Promise<void> {
  await api<void>(`/drivers/${driverId}/documents`, { method: 'POST', body: JSON.stringify(input) });
}

export function getDriverDocumentDownloadUrl(driverId: string, documentTypeCode: string): Promise<{ url: string }> {
  return api<{ url: string }>(`/drivers/${driverId}/documents/${documentTypeCode}/download-url`);
}

/** A bare `fetch` to the presigned S3 URL — not the app's `api()` helper, which targets `/api/v1` and adds CSRF headers a third-party endpoint doesn't want. */
export async function uploadToPresignedUrl(upload: PresignedUpload, file: File): Promise<void> {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => form.append(key, value));
  form.append('file', file);

  const res = await fetch(upload.url, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`Upload failed with status ${res.status}`);
}
